import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { DotsHorizontalIcon } from "@heroicons/react/solid";

const DROPDOWN_WIDTHS = {
  "w-40": 160,
  "w-48": 192,
  "w-56": 224,
};

export default function ActionDropdown({
  items = [],
  buttonClassName = "",
  dropdownWidth = "w-48",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const dropdownId = useRef(
    `dropdown-${Math.random().toString(36).substr(2, 9)}`,
  );

  const getPosition = (rect) => {
    const dropdownWidthPx = DROPDOWN_WIDTHS[dropdownWidth] || 192;
    const gap = 4;
    const estimatedHeight = items.length * 36 + 8;

    let top = rect.bottom + gap;
    let left = rect.right - dropdownWidthPx;

    if (left < gap) left = gap;
    if (left + dropdownWidthPx > window.innerWidth - gap) {
      left = window.innerWidth - dropdownWidthPx - gap;
    }

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    if (spaceBelow < estimatedHeight && spaceAbove > estimatedHeight) {
      top = rect.top - estimatedHeight - gap;
    }

    return { top, left };
  };

  const openDropdown = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPosition(getPosition(rect));
    setIsOpen(true);
  };

  const closeDropdown = () => setIsOpen(false);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (buttonRef.current && !buttonRef.current.contains(e.target)) {
        const dropdown = document.getElementById(dropdownId.current);
        if (dropdown && !dropdown.contains(e.target)) {
          closeDropdown();
        }
      }
    };

    const handleReposition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setPosition(getPosition(rect));
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [isOpen, items]);

  return (
    <div className="relative inline-flex">
      <button
        ref={buttonRef}
        onClick={(e) => (isOpen ? closeDropdown() : openDropdown(e))}
        className={`inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none ${buttonClassName}`}
      >
        <DotsHorizontalIcon className="h-5 w-5" />
      </button>
      {isOpen &&
        createPortal(
          <div
            id={dropdownId.current}
            className={`fixed rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-[9999] py-1 ${dropdownWidth}`}
            style={{ top: position.top, left: position.left }}
          >
            {items.map((item, i) => {
              const Icon = item.icon;
              return (
                <button
                  key={i}
                  onClick={() => {
                    item.onClick?.();
                    closeDropdown();
                  }}
                  disabled={item.disabled}
                  className={`flex w-full items-center px-4 py-2 text-sm ${
                    item.className || "text-gray-700 hover:bg-gray-100"
                  } ${item.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {Icon && <Icon className="h-4 w-4 mr-2 flex-shrink-0" />}
                  {item.label}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}
